import React, { useEffect, useState, useMemo, forwardRef } from "react";
import axios from "axios";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BASE_URL from "BASE_URL";

import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Avatar,
  Tooltip,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  TextField,
  Paper,
  Collapse,
  Slide,
  Grow,
  Zoom,
  Fade,
} from "@mui/material";

import { keyframes } from "@mui/system";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";
import ArticleIcon from "@mui/icons-material/Article";
import UpdateIcon from "@mui/icons-material/Update";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";

// Animations (same as before)
const slideUp = keyframes`...`;
const glow = keyframes`...`;
const slideInUp = keyframes`...`;
const float = keyframes`...`;
const pulseRing = keyframes`...`;
const shakeAnimation = keyframes`...`;
const shimmer = keyframes`...`;
const bounceIn = keyframes`...`;

// Transition component
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={500} />;
});

function PostManagement() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deletePostTitle, setDeletePostTitle] = useState("");
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    searchTitle: "",
    searchId: "",
    mainCategory: "",
    subCategory: "",
    childCategory: "",
    postType: "",
    status: "",
  });

  const [filterOptions, setFilterOptions] = useState({
    mainCategories: [],
    subCategories: [],
    childCategories: [],
    postTypes: ["sell", "buy"],
    statuses: ["active", "inactive", "pending"],
  });

  const token = localStorage.getItem("token");

  // Fetch all posts
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/admin/allposts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setPosts(response.data.data);
        setFilteredPosts(response.data.data);
        extractFilterOptions(response.data.data);
      } else {
        showSnackbar(response.data.message || "Failed to fetch posts", "error");
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      showSnackbar(error.response?.data?.message || "Error fetching posts", "error");
    } finally {
      setLoading(false);
    }
  };

  const extractFilterOptions = (postsData) => {
    const mainCategories = new Set();
    const subCategories = new Set();
    const childCategories = new Set();

    postsData.forEach((post) => {
      if (post.mainCategory?.name) mainCategories.add(post.mainCategory.name);
      if (post.subCategory?.name) subCategories.add(post.subCategory.name);
      if (post.childCategory?.name) childCategories.add(post.childCategory.name);
    });

    setFilterOptions({
      mainCategories: Array.from(mainCategories).sort(),
      subCategories: Array.from(subCategories).sort(),
      childCategories: Array.from(childCategories).sort(),
      postTypes: ["sell", "buy"],
      statuses: ["active", "inactive", "pending"],
    });
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  // 🔥 REAL-TIME FILTERING: apply filters whenever any filter changes
  useEffect(() => {
    let filtered = [...posts];

    if (filters.searchTitle.trim()) {
      filtered = filtered.filter((post) =>
        post.title?.toLowerCase().includes(filters.searchTitle.toLowerCase())
      );
    }

    if (filters.searchId.trim()) {
      const searchIdLast5 = filters.searchId.trim().toLowerCase();
      filtered = filtered.filter((post) => {
        const last5 = post._id?.slice(-5).toLowerCase();
        return last5 === searchIdLast5;
      });
    }

    if (filters.mainCategory) {
      filtered = filtered.filter((post) => post.mainCategory?.name === filters.mainCategory);
    }

    if (filters.subCategory) {
      filtered = filtered.filter((post) => post.subCategory?.name === filters.subCategory);
    }

    if (filters.childCategory) {
      filtered = filtered.filter((post) => post.childCategory?.name === filters.childCategory);
    }

    if (filters.postType) {
      filtered = filtered.filter((post) => post.post_type === filters.postType);
    }

    if (filters.status) {
      filtered = filtered.filter((post) => post.status === filters.status);
    }

    setFilteredPosts(filtered);
  }, [filters, posts]); // runs on every filter change or posts update

  const resetFilters = () => {
    setFilters({
      searchTitle: "",
      searchId: "",
      mainCategory: "",
      subCategory: "",
      childCategory: "",
      postType: "",
      status: "",
    });
    showSnackbar("All filters cleared", "info");
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Delete & status handlers (unchanged)
  const handleOpenDeleteDialog = (id, title) => {
    setDeleteId(id);
    setDeletePostTitle(title);
    setDeleteDialogOpen(true);
  };

  const handleDeletePost = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.delete(`${BASE_URL}/admin/delete-post?id=${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        showSnackbar("Post deleted successfully!", "success");
        fetchPosts();
        setDeleteDialogOpen(false);
      } else {
        showSnackbar(response.data.message || "Delete failed", "error");
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Failed to delete post", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenStatusDialog = (post) => {
    setSelectedPost(post);
    setNewStatus(post.status);
    setStatusDialogOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      showSnackbar("Please select a status", "warning");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/admin/update-post-status?id=${selectedPost._id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        showSnackbar(`Post status updated to ${newStatus} successfully!`, "success");
        fetchPosts();
        setStatusDialogOpen(false);
      } else {
        showSnackbar(response.data.message || "Status update failed", "error");
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Failed to update post status", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "active":
        return {
          label: "ACTIVE",
          color: "success",
          icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
        };
      case "inactive":
        return { label: "INACTIVE", color: "error", icon: <CancelIcon sx={{ fontSize: 16 }} /> };
      case "pending":
        return { label: "PENDING", color: "warning", icon: <PendingIcon sx={{ fontSize: 16 }} /> };
      default:
        return { label: status?.toUpperCase() || "UNKNOWN", color: "default", icon: null };
    }
  };

  const getPostTypeChip = (type) => {
    return type === "sell" ? (
      <Chip label="SELL" size="small" color="error" variant="gradient" />
    ) : (
      <Chip label="BUY" size="small" color="info" variant="gradient" />
    );
  };

  const columns = [
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          POST DETAILS
        </MDTypography>
      ),
      accessor: "postDetails",
      align: "left",
      width: "25%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          POSTED BY
        </MDTypography>
      ),
      accessor: "postedBy",
      align: "left",
      width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          CATEGORY
        </MDTypography>
      ),
      accessor: "category",
      align: "left",
      width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          PRICE & QTY
        </MDTypography>
      ),
      accessor: "priceQty",
      align: "center",
      width: "12%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          POST TYPE
        </MDTypography>
      ),
      accessor: "postType",
      align: "center",
      width: "8%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          STATUS
        </MDTypography>
      ),
      accessor: "status",
      align: "center",
      width: "10%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          CREATED
        </MDTypography>
      ),
      accessor: "createdAt",
      align: "center",
      width: "10%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          ACTIONS
        </MDTypography>
      ),
      accessor: "action",
      align: "center",
      width: "10%",
    },
  ];

  const rows = useMemo(
    () =>
      filteredPosts
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .map((post, index) => {
          const statusInfo = getStatusChip(post.status);
          const last5Digits = post._id?.slice(-5).toUpperCase() || "N/A";
          return {
            postDetails: (
              <MDBox>
                <MDTypography variant="button" fontWeight="bold" color="dark">
                  {post.title}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  color="text"
                  display="block"
                  fontWeight="medium"
                  sx={{ fontSize: "0.7rem" }}
                >
                  ID: #{last5Digits}
                </MDTypography>
                {/* <MDTypography variant="caption" color="text" display="block" sx={{ mt: 0.5 }}>
                {post.description?.length > 30
                  ? `${post.description.substring(0, 30)}...`
                  : post.description}
              </MDTypography> */}
              </MDBox>
            ),
            postedBy: (
              <MDBox display="flex" alignItems="center" gap={1}>
                <Avatar
                  src={post.user?.profile_photo}
                  alt={post.user?.name}
                  sx={{ width: 32, height: 32 }}
                />
                <MDBox>
                  <MDTypography variant="caption" fontWeight="bold" display="block">
                    {post.user?.name || "N/A"}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="medium"
                    fontSize="0.7rem"
                  >
                    {post.user?.email_id}
                  </MDTypography>
                </MDBox>
              </MDBox>
            ),
            category: (
              <MDBox>
                {/* <MDTypography variant="caption" fontWeight="medium" display="block">
                {post.mainCategory?.name || "N/A"}
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text"
                fontWeight="medium"
                fontSize="0.7rem"
                display="block"
              >
                {post.subCategory?.name}
              </MDTypography> */}
                {post.childCategory && (
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="medium"
                    fontSize="0.7rem"
                    display="block"
                  >
                    {post.childCategory?.name}
                  </MDTypography>
                )}
              </MDBox>
            ),
            priceQty: (
              <MDBox textAlign="center">
                <MDTypography variant="button" fontWeight="bold" color="primary">
                  ₹{post.price}/{post.unitType}
                </MDTypography>
                <MDTypography variant="caption" color="text" fontWeight="medium" display="block">
                  Qty: {post.quantity}/{post.unitType}
                </MDTypography>
              </MDBox>
            ),
            postType: <MDBox textAlign="center">{getPostTypeChip(post.post_type)}</MDBox>,
            status: (
              <MDBox textAlign="center">
                <Chip
                  label={statusInfo.label}
                  color={statusInfo.color}
                  size="small"
                  icon={statusInfo.icon}
                  sx={{ fontWeight: "bold", color: "#fff" }}
                />
                {!post.is_admin_approved && (
                  <Tooltip title="Admin Approval Pending">
                    <PendingIcon sx={{ fontSize: 14, color: "#ff9800", ml: 0.5 }} />
                  </Tooltip>
                )}
              </MDBox>
            ),
            createdAt: (
              <MDTypography variant="caption" fontWeight="medium" textAlign="center">
                {formatDate(post.createdAt)}
              </MDTypography>
            ),
            action: (
              <MDBox display="flex" justifyContent="center">
                <Tooltip title="View Details">
                  <IconButton
                    color="info"
                    onClick={() => navigate(`/post-details/${post._id}`)}
                    size="small"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Post">
                  <IconButton
                    color="warning"
                    onClick={() => navigate(`/edit-post/${post._id}`)}
                    size="small"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Update Status">
                  <IconButton
                    color="info"
                    onClick={() => handleOpenStatusDialog(post)}
                    size="small"
                  >
                    <UpdateIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Post">
                  <IconButton
                    color="error"
                    onClick={() => handleOpenDeleteDialog(post._id, post.title)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </MDBox>
            ),
          };
        }),
    [filteredPosts]
  );

  const activeFiltersCount = Object.values(filters).filter((v) => v && v !== "").length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "20px" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <ArticleIcon sx={{ color: "#fff", fontSize: 28 }} />
                  <MDTypography variant="h6" color="white">
                    Post Management
                  </MDTypography>
                </MDBox>
              </MDBox>

              {/* Filter Section - Real-time */}
              <Collapse in={!showFilters}>
                <Grid container spacing={3} sx={{ pt: 3, pl: 3, pr: 3 }}>
                  <Grid item xs={12} md={3} lg={3}>
                    <TextField
                      fullWidth
                      label="Search by Post Title"
                      variant="outlined"
                      size="small"
                      value={filters.searchTitle}
                      onChange={(e) => handleFilterChange("searchTitle", e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3} lg={3}>
                    <TextField
                      fullWidth
                      label="Post ID (last 5 digits)"
                      variant="outlined"
                      size="small"
                      placeholder="e.g., ABC12"
                      value={filters.searchId}
                      onChange={(e) => handleFilterChange("searchId", e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
                      }}
                      // helperText="Enter exact last 5 characters"
                    />
                  </Grid>

                  {/* <Grid item xs={12} md={3} lg={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Main Category</InputLabel>
                      <Select
                        value={filters.mainCategory}
                        onChange={(e) => handleFilterChange("mainCategory", e.target.value)}
                        label="Main Category"
                        sx={{ height: "37px" }}
                      >
                        <MenuItem value="">All</MenuItem>
                        {filterOptions.mainCategories.map((cat) => (
                          <MenuItem key={cat} value={cat}>
                            {cat}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={3} lg={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sub Category</InputLabel>
                      <Select
                        value={filters.subCategory}
                        onChange={(e) => handleFilterChange("subCategory", e.target.value)}
                        label="Sub Category"
                        sx={{ height: "37px" }}
                      >
                        <MenuItem value="">All</MenuItem>
                        {filterOptions.subCategories.map((cat) => (
                          <MenuItem key={cat} value={cat}>
                            {cat}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid> */}

                  <Grid item xs={12} md={3} lg={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Child Category</InputLabel>
                      <Select
                        value={filters.childCategory}
                        onChange={(e) => handleFilterChange("childCategory", e.target.value)}
                        label="Child Category"
                        sx={{ height: "37px" }}
                      >
                        <MenuItem value="">All</MenuItem>
                        {filterOptions.childCategories.map((cat) => (
                          <MenuItem key={cat} value={cat}>
                            {cat}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={3} lg={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Post Type</InputLabel>
                      <Select
                        value={filters.postType}
                        onChange={(e) => handleFilterChange("postType", e.target.value)}
                        label="Post Type"
                        sx={{ height: "37px" }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="sell">Sell</MenuItem>
                        <MenuItem value="buy">Buy</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={3} lg={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filters.status}
                        onChange={(e) => handleFilterChange("status", e.target.value)}
                        label="Status"
                        sx={{ height: "37px" }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={3} lg={2}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={resetFilters}
                      startIcon={<ClearIcon />}
                      fullWidth
                    >
                      Clear
                    </MDButton>
                  </Grid>
                </Grid>
              </Collapse>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : filteredPosts.length === 0 ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <MDTypography variant="h6" color="dark" fontWeight="medium">
                      No posts found matching the filters.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Status Update Dialog (unchanged) */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => !isSubmitting && setStatusDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: "0 40px 80px rgba(0,0,0,0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #2196f3, #64b5f6, #2196f3)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setStatusDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            "&:hover": { transform: "rotate(90deg) scale(1.1)", bgcolor: "rgba(33,150,243,0.1)" },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon />
        </IconButton>
        <MDBox
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: "radial-gradient(circle, rgba(33,150,243,0.08) 0%, rgba(33,150,243,0) 70%)",
            pointerEvents: "none",
          }}
        />
        <DialogTitle sx={{ pb: 1, pt: 3, position: "relative", zIndex: 1 }}>
          <Grow in={statusDialogOpen} timeout={600}>
            <MDBox display="flex" alignItems="center" gap={2}>
              <Zoom in={statusDialogOpen} timeout={600}>
                <MDBox
                  sx={{
                    background: "linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)",
                    width: 56,
                    height: 56,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 20px rgba(33,150,243,0.3)",
                    animation: `${glow} 2s ease-in-out infinite`,
                  }}
                >
                  <UpdateIcon sx={{ fontSize: 32, color: "white" }} />
                </MDBox>
              </Zoom>
              <MDBox>
                <MDTypography
                  variant="h4"
                  fontWeight="bold"
                  sx={{
                    background: "linear-gradient(135deg, #2196f3 0%, #1565c0 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    mb: 0.5,
                  }}
                >
                  Update Post Status
                </MDTypography>
                <MDTypography variant="body2" color="textSecondary">
                  Modify the status of this post
                </MDTypography>
              </MDBox>
            </MDBox>
          </Grow>
        </DialogTitle>
        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={statusDialogOpen} timeout={800}>
            <MDBox pt={3} pb={2}>
              <Stack spacing={3}>
                <Zoom in={statusDialogOpen} timeout={600} style={{ transitionDelay: "100ms" }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "16px",
                      background:
                        "linear-gradient(135deg, rgba(33,150,243,0.05) 0%, rgba(33,150,243,0.02) 100%)",
                      border: "1px solid rgba(33,150,243,0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateX(5px)",
                        borderColor: "rgba(33,150,243,0.3)",
                      },
                    }}
                  >
                    <MDBox display="flex" alignItems="flex-start" gap={1.5}>
                      <ArticleIcon sx={{ color: "#2196f3", mt: 0.5 }} />
                      <MDBox flex={1}>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="bold"
                          display="block"
                        >
                          POST TITLE
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                          {selectedPost?.title}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </Paper>
                </Zoom>
                <Zoom in={statusDialogOpen} timeout={600} style={{ transitionDelay: "150ms" }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "16px",
                      background:
                        "linear-gradient(135deg, rgba(33,150,243,0.05) 0%, rgba(33,150,243,0.02) 100%)",
                      border: "1px solid rgba(33,150,243,0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateX(5px)",
                        borderColor: "rgba(33,150,243,0.3)",
                      },
                    }}
                  >
                    <MDBox display="flex" alignItems="flex-start" gap={1.5}>
                      <Avatar sx={{ bgcolor: "#2196f3", width: 40, height: 40 }}>
                        <PersonIcon />
                      </Avatar>
                      <MDBox flex={1}>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="bold"
                          display="block"
                        >
                          POSTED BY
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                          {selectedPost?.user?.name}
                        </MDTypography>
                        <MDBox display="flex" alignItems="center" gap={1} mt={1}>
                          <EmailIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                          <MDTypography variant="caption" fontWeight="medium" color="textSecondary">
                            {selectedPost?.user?.email_id}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Paper>
                </Zoom>
                <Grow in={statusDialogOpen} timeout={600} style={{ transitionDelay: "200ms" }}>
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="bold" color="text" mb={1}>
                      Select New Status
                    </MDTypography>
                    <FormControl fullWidth>
                      <Select
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                        disabled={isSubmitting}
                        sx={{ borderRadius: "12px", height: "44px" }}
                      >
                        <MenuItem value="active">
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 20 }} />
                            <MDTypography variant="body2" fontWeight="medium">
                              Active
                            </MDTypography>
                          </MDBox>
                        </MenuItem>
                        <MenuItem value="inactive">
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <CancelIcon sx={{ color: "#f44336", fontSize: 20 }} />
                            <MDTypography variant="body2" fontWeight="medium">
                              Inactive
                            </MDTypography>
                          </MDBox>
                        </MenuItem>
                        <MenuItem value="pending">
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <PendingIcon sx={{ color: "#ff9800", fontSize: 20 }} />
                            <MDTypography variant="body2" fontWeight="medium">
                              Pending
                            </MDTypography>
                          </MDBox>
                        </MenuItem>
                      </Select>
                    </FormControl>
                    {newStatus && (
                      <Fade in={!!newStatus}>
                        <MDBox mt={2} p={2} borderRadius="12px" bgcolor="rgba(33,150,243,0.05)">
                          <MDTypography variant="caption" color="text" fontWeight="bold">
                            Preview:
                          </MDTypography>
                          <Chip
                            label={newStatus.toUpperCase()}
                            color={
                              newStatus === "active"
                                ? "success"
                                : newStatus === "inactive"
                                ? "error"
                                : "warning"
                            }
                            size="small"
                            sx={{ ml: 1, fontWeight: "bold" }}
                          />
                        </MDBox>
                      </Fade>
                    )}
                  </MDBox>
                </Grow>
              </Stack>
            </MDBox>
          </Fade>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            gap: 2,
            justifyContent: "flex-end",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={statusDialogOpen} timeout={600} style={{ transitionDelay: "250ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => setStatusDialogOpen(false)}
              disabled={isSubmitting}
              sx={{
                borderRadius: "12px",
                px: 3,
                py: 1,
                fontWeight: "bold",
                textTransform: "none",
                borderWidth: "2px",
                color: "rgba(22, 22, 22, 0.3)",
                borderColor: "rgba(0, 0, 0, 0.3)",
                "&:hover": { transform: "translateY(-2px)", borderColor: "rgba(0, 0, 0, 0.3)" },
              }}
            >
              Cancel
            </MDButton>
          </Zoom>
          <Zoom in={statusDialogOpen} timeout={600} style={{ transitionDelay: "300ms" }}>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleUpdateStatus}
              disabled={isSubmitting}
              sx={{
                borderRadius: "12px",
                px: 4,
                py: 1,
                fontWeight: "bold",
                textTransform: "none",
                boxShadow: "0 6px 14px rgba(33,150,243,0.4)",
                "&:hover": { transform: "translateY(-2px)" },
              }}
              startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
            >
              {isSubmitting ? "Updating..." : "Update Status"}
            </MDButton>
          </Zoom>
        </DialogActions>
        <MDBox
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "linear-gradient(90deg, #2196f3, #64b5f6, #2196f3)",
            animation: `${shimmer} 2s infinite linear`,
            backgroundSize: "200% 100%",
          }}
        />
      </Dialog>

      {/* Delete Confirmation Dialog (unchanged) */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isSubmitting && setDeleteDialogOpen(false)}
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "32px",
            background: "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)",
            boxShadow: "0 40px 80px rgba(0,0,0,0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setDeleteDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            "&:hover": { transform: "rotate(90deg) scale(1.1)", bgcolor: "rgba(239,68,68,0.1)" },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon sx={{ color: "#ef4444" }} />
        </IconButton>
        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: "radial-gradient(circle, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0) 70%)",
            pointerEvents: "none",
          }}
        />
        {[...Array(15)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: `${Math.random() * 6 + 2}px`,
              height: `${Math.random() * 6 + 2}px`,
              background: `rgba(239,68,68,${Math.random() * 0.3 + 0.1})`,
              borderRadius: "50%",
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `${float} ${Math.random() * 5 + 3}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
              pointerEvents: "none",
            }}
          />
        ))}
        <DialogTitle sx={{ pt: 4, pb: 2, textAlign: "center", position: "relative", zIndex: 1 }}>
          <Grow in={deleteDialogOpen} timeout={600}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Zoom in={deleteDialogOpen} timeout={600}>
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    borderRadius: "50%",
                    animation: `${pulseRing} 2s infinite`,
                    cursor: "pointer",
                    mb: 2,
                    "&:hover": { animation: `${shakeAnimation} 0.5s ease-in-out` },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 20px rgba(239,68,68,0.4)",
                      animation: `${glow} 2s ease-in-out infinite`,
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 40, color: "#fff" }} />
                  </Box>
                </Box>
              </Zoom>
              <Box sx={{ animation: `${bounceIn} 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)` }}>
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    display: "inline-block",
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold">
                    Delete Post?
                  </MDTypography>
                </Box>
              </Box>
            </Box>
          </Grow>
        </DialogTitle>
        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={deleteDialogOpen} timeout={800}>
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "100ms" }}>
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{
                    borderRadius: "16px",
                    mb: 3,
                    backgroundColor: "rgba(239,68,68,0.1)",
                    border: "1px solid rgba(239,68,68,0.2)",
                    "& .MuiAlert-icon": { color: "#ef4444" },
                  }}
                >
                  <MDTypography variant="body2" fontWeight="medium" color="error">
                    This action cannot be undone!
                  </MDTypography>
                </Alert>
              </Zoom>
              <Grow in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "150ms" }}>
                <Box
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(239,68,68,0.05) 0%, rgba(239,68,68,0.02) 100%)",
                    borderRadius: "20px",
                    p: 3,
                    border: "1px solid rgba(239,68,68,0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "scale(1.02)", borderColor: "rgba(239,68,68,0.4)" },
                  }}
                >
                  <MDTypography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    fontWeight="bold"
                  >
                    POST TO BE DELETED
                  </MDTypography>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: "#ef4444", wordBreak: "break-word" }}
                  >
                    {deletePostTitle}
                  </MDTypography>
                </Box>
              </Grow>
              <Fade in={deleteDialogOpen} timeout={800} style={{ transitionDelay: "200ms" }}>
                <Box sx={{ mt: 3 }}>
                  <MDTypography
                    variant="body2"
                    color="textSecondary"
                    sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                    This post will be permanently removed from the system
                  </MDTypography>
                </Box>
              </Fade>
            </Box>
          </Fade>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 4,
            pt: 2,
            gap: 2,
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "250ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                borderWidth: "2px",
                borderColor: "#d1d5db",
                color: "#374151",
                "&:hover": {
                  transform: "translateY(-2px)",
                  borderColor: "#ef4444",
                  backgroundColor: "rgba(239,68,68,0.05)",
                },
              }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </MDButton>
          </Zoom>
          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "300ms" }}>
            <MDButton
              variant="contained"
              color="error"
              onClick={handleDeletePost}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                boxShadow: "0 6px 14px rgba(239,68,68,0.4)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 20px rgba(239,68,68,0.5)",
                  backgroundColor: "#dc2626",
                },
                "&:active": { transform: "translateY(0)" },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "-100%",
                  // width: "100%",
                  // height: "100%",
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                  transition: "left 0.5s ease",
                },
                "&:hover::before": { left: "100%" },
              }}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />
              }
            >
              {isSubmitting ? "Deleting..." : "Delete Permanently"}
            </MDButton>
          </Zoom>
        </DialogActions>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
            animation: `${shimmer} 2s infinite linear`,
            backgroundSize: "200% 100%",
          }}
        />
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "8px" }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default PostManagement;
